import { useMemo } from 'react';

import { WithChildren } from '../../../core/types/with-children.js';
import {
  XYChartOverlayPerformanceContext,
  type SeriesToQuadtree,
} from '../context/XYChartOverlayPerformance.context.js';
import { useBuildHoverGrid } from '../hooks/useBuildHoverGrid.js';
import { useBuildOverlayStrategy } from '../hooks/useBuildOverlayStrategy.js';
import { useXYChartData } from '../hooks/useXYChartData.js';
import { useXYChartScales } from '../hooks/useXYChartScales.js';
import { useXYChartTooltip } from '../hooks/useXYChartTooltip.js';
import type { SortedEntriesPerShape } from '../utils/build-sorted-entries.js';
import { buildQueryClosest } from '../utils/query-closest-quadtree.js';
import { buildQueryGetClosestPoint } from '../utils/query-get-closest-point.js';
import {
  buildQueryAllHoveredFromGrid,
  buildQueryHoveredFromGrid,
} from '../utils/query-hovered-grid.js';
import {
  buildQueryAllHovered,
  buildQueryHovered,
} from '../utils/query-hovered.js';
import { buildQueryShared } from '../utils/query-shared.js';
import { buildQuerySingle } from '../utils/query-single.js';

export const XYChartOverlayPerformanceProvider = ({
  children,
}: WithChildren) => {
  const axisIdToScales = useXYChartScales();
  const { datapointMetadataMap, axisIdToScaleDomain, seriesToMetadata } =
    useXYChartData();
  const { variant } = useXYChartTooltip();
  const isSharedVariant = variant === 'shared';

  const { buildResult, getBarPixelCenter, getLinearSeriesPixelCenter } =
    useBuildOverlayStrategy();
  const seriesIdToHoverGrid = useBuildHoverGrid();

  const sortedEntries = isSharedVariant
    ? (buildResult as SortedEntriesPerShape | undefined)
    : undefined;
  const seriesIdToTree = isSharedVariant
    ? undefined
    : (buildResult as SeriesToQuadtree | undefined);

  const context = useMemo(() => {
    if (isSharedVariant) {
      const queryAllHovered = seriesIdToHoverGrid
        ? buildQueryAllHoveredFromGrid(seriesIdToHoverGrid)
        : buildQueryAllHovered(
            axisIdToScales,
            datapointMetadataMap,
            seriesToMetadata,
            axisIdToScaleDomain,
          );
      return {
        query: buildQueryShared(
          sortedEntries,
          queryAllHovered,
          getBarPixelCenter,
          getLinearSeriesPixelCenter,
          datapointMetadataMap,
        ),
      };
    }

    const queryHovered = seriesIdToHoverGrid
      ? buildQueryHoveredFromGrid(seriesIdToHoverGrid)
      : buildQueryHovered(
          axisIdToScales,
          datapointMetadataMap,
          seriesToMetadata,
          axisIdToScaleDomain,
        );

    const queryClosest = seriesIdToTree
      ? buildQueryClosest(
          seriesIdToTree,
          axisIdToScales,
          seriesToMetadata,
          axisIdToScaleDomain,
          datapointMetadataMap,
        )
      : buildQueryGetClosestPoint(
          axisIdToScales,
          seriesToMetadata,
          datapointMetadataMap,
          axisIdToScaleDomain,
        );
    return { query: buildQuerySingle(queryClosest, queryHovered) };
  }, [
    axisIdToScales,
    datapointMetadataMap,
    seriesToMetadata,
    axisIdToScaleDomain,
    isSharedVariant,
    sortedEntries,
    getBarPixelCenter,
    getLinearSeriesPixelCenter,
    seriesIdToTree,
    seriesIdToHoverGrid,
  ]);

  return (
    <XYChartOverlayPerformanceContext.Provider value={context}>
      {children}
    </XYChartOverlayPerformanceContext.Provider>
  );
};

XYChartOverlayPerformanceProvider.displayName =
  'XYChartOverlayPerformanceProvider';
